import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { STEM_ELEMENT, type Stem, type Element, type FourPillars } from '@/utils/baziCalc';
import { ELEMENT_COLORS } from '@/utils/calcTools/bazi/shishenEngine';
import type { ServerInsightData } from '@/pages/experience/serverInsight';
import { convertText } from '@/utils/langConvert';

interface Props {
  type: 'rizhu' | 'wangshuai' | 'yinyang' | 'geju' | 'xiyong' | 'jishen';
  isOpen: boolean;
  onClose: () => void;
  isDark?: boolean;
  fourPillars?: FourPillars;
  geju?: ServerInsightData['geju'];
  xiyong?: ServerInsightData['xiyong'];
  rizhu?: ServerInsightData['rizhu'];
  yinYangRef?: string;
  gender?: string;
  dayStem?: string;
}

export default function BaziInsightModal({
  type,
  isOpen,
  onClose,
  isDark = true,
  fourPillars,
  geju,
  xiyong,
  rizhu,
  yinYangRef,
  gender,
  dayStem,
}: Props) {
  const { t, i18n } = useTranslation('paipan');
  const isZh = i18n.language.startsWith('zh');
  const trd = (text: string) => convertText(text, i18n.language);

  if (!isOpen) return null;

  const overlayBg = isDark ? 'rgba(15,10,18,0.75)' : 'rgba(60,50,40,0.55)';
  const cardBg = isDark ? '#1A1420' : 'var(--ink-paper)';
  const cardBorder = isDark ? 'rgba(183,154,197,0.25)' : 'rgba(232,160,32,0.25)';
  const titleColor = 'var(--accent-secondary)';
  const textColor = 'var(--text-primary)';
  const labelColor = 'var(--text-secondary)';
  const sectionBg = isDark ? 'rgba(183,154,197,0.06)' : 'rgba(232,160,32,0.06)';

  const titles: Record<string, string> = {
    rizhu: isZh ? trd('日主属性') : 'Day Master',
    wangshuai: isZh ? trd('旺衰参考') : 'Strength Analysis',
    yinyang: isZh ? trd('阴阳参考') : 'Yin-Yang Balance',
    geju: isZh ? trd('格局参考') : 'Pattern Analysis',
    xiyong: isZh ? trd('喜用神') : 'Favorable Elements',
    jishen: isZh ? trd('忌神') : 'Unfavorable Elements',
  };

  const renderEmpty = () => (
    <p className="text-sm text-center py-6" style={{ color: labelColor }}>{isZh ? trd('暂无数据') : 'No Data'}</p>
  );

  const renderContent = () => {
    switch (type) {
      case 'rizhu': {
        return (
          <div className="space-y-3">
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl font-bold" style={{ color: rizhu?.elementColor || titleColor, fontFamily: "'Noto Serif TC', serif" }}>{dayStem}</span>
                <span className="text-sm" style={{ color: labelColor }}>{rizhu?.subtitle || (isZh ? trd('日主属性') : 'Day Master')}</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: textColor }}>
                {rizhu?.overview || (isZh ? trd('日主为命局核心，代表个人本质与精力。') : 'The day master is the core of the chart.')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl p-2.5" style={{ background: sectionBg }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <i className="ri-thumb-up-line text-xs" style={{ color: '#7BC67E' }} />
                  <span className="text-xs font-bold" style={{ color: '#7BC67E' }}>{isZh ? trd('优势') : 'Strengths'}</span>
                </div>
                <ul className="space-y-1">
                  {(rizhu?.strengths?.length ? rizhu.strengths : []).map((s, i) => (
                    <li key={i} className="text-[11px] leading-relaxed" style={{ color: textColor }}>· {s}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl p-2.5" style={{ background: sectionBg }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <i className="ri-error-warning-line text-xs" style={{ color: '#D14B45' }} />
                  <span className="text-xs font-bold" style={{ color: '#D14B45' }}>{isZh ? trd('注意') : 'Weaknesses'}</span>
                </div>
                <ul className="space-y-1">
                  {(rizhu?.weaknesses?.length ? rizhu.weaknesses : []).map((w, i) => (
                    <li key={i} className="text-[11px] leading-relaxed" style={{ color: textColor }}>· {w}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      }
      case 'wangshuai': {
        if (!xiyong) return renderEmpty();
        const strengthLabel = xiyong.bodyStrength === 'strong' ? trd('偏旺') : xiyong.bodyStrength === 'weak' ? trd('偏弱') : trd('中和');
        const strengthColor = xiyong.bodyStrength === 'strong' ? '#D14B45' : xiyong.bodyStrength === 'weak' ? '#7A9ECF' : '#C8A96E';
        const strengthDesc = xiyong.bodyStrength === 'strong'
          ? trd('日主能量充沛，五行生扶有力，适合主动输出、承担主导角色。')
          : xiyong.bodyStrength === 'weak'
          ? trd('日主能量不足，需要借助印星、比劫等力量来增强自身。')
          : trd('日主能量平衡，灵活多变，可根据环境调整策略。');
        return (
          <div className="space-y-3">
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${strengthColor}20`, border: `2px solid ${strengthColor}55` }}>
                  <span className="text-sm font-bold" style={{ color: strengthColor }}>{strengthLabel}</span>
                </div>
                <div>
                  <span className="text-sm font-bold" style={{ color: textColor }}>{isZh ? trd('身强弱判定') : 'Body Strength'}</span>
                  <p className="text-[10px]" style={{ color: labelColor }}>{isZh ? trd('基于四柱五行能量综合计算') : 'Based on overall energy calculation'}</p>
                </div>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: textColor }}>{strengthDesc}</p>
            </div>
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className="ri-lightbulb-line text-xs" style={{ color: titleColor }} />
                <span className="text-xs font-bold" style={{ color: titleColor }}>{isZh ? trd('调候建议') : 'Adjustment Advice'}</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: textColor }}>
                {xiyong.bodyStrength === 'strong'
                  ? trd('身强之命，宜泄耗，喜用神以食伤、财官为用。')
                  : xiyong.bodyStrength === 'weak'
                  ? trd('身弱之命，宜生扶，喜用神以印星、比劫为用。')
                  : trd('中和之命，宜随运而变，灵活取用。')}
              </p>
            </div>
          </div>
        );
      }
      case 'yinyang': {
        if (!fourPillars || !yinYangRef) return renderEmpty();
        const YANG_STEMS = new Set(['甲', '丙', '戊', '庚', '壬']);
        const YANG_BRANCHES = new Set(['子', '寅', '辰', '午', '申', '戌']);
        const yangCount = [fourPillars.year, fourPillars.month, fourPillars.day, fourPillars.hour].reduce((count, p) => (
          count + (YANG_STEMS.has(p.stem) ? 1 : 0) + (YANG_BRANCHES.has(p.branch) ? 1 : 0)
        ), 0);
        const yinCount = 8 - yangCount;
        const ratio = yangCount / 8;
        const isFemale = gender?.includes('女') || gender === 'female';
        const yinYangDesc = yangCount >= 7
          ? trd('阳气极盛，性格外向主动，行动力充沛，但需注意情绪管理。')
          : yangCount === 6
          ? isFemale
            ? trd('阳气极盛，性格外向主动，行动力充沛，但需注意情绪管理。')
            : trd('阳气偏旺，积极进取，但偶有过激。')
          : ratio >= 0.58
          ? trd('阳气略盛，偏向主动，宜培养耐心。')
          : ratio <= 0.25
          ? trd('阴气极盛，性格内敛细腻，思维深沉，需加强行动力。')
          : ratio <= 0.42
          ? trd('阴气偏旺，稳重谨慎，偶显犹豫。')
          : trd('阴阳平衡，刚柔并济，适应能力较强。');

        return (
          <div className="space-y-3">
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold" style={{ color: textColor }}>{isZh ? trd('阴阳比例') : 'Yin-Yang Ratio'}</span>
                <span className="text-sm font-bold" style={{ color: titleColor }}>{yinYangRef}</span>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden mb-2" style={{ background: isDark ? 'rgba(107,78,113,0.3)' : 'rgba(232,160,32,0.12)' }}>
                <div className="h-full" style={{ width: `${(yangCount / 8) * 100}%`, background: '#D14B45' }} />
                <div className="h-full" style={{ width: `${(yinCount / 8) * 100}%`, background: '#7A9ECF' }} />
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span style={{ color: '#D14B45' }}>{trd('阳')} {yangCount}/8</span>
                <span style={{ color: '#7A9ECF' }}>{trd('阴')} {yinCount}/8</span>
              </div>
            </div>
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className="ri-file-list-3-line text-xs" style={{ color: titleColor }} />
                <span className="text-xs font-bold" style={{ color: titleColor }}>{isZh ? trd('说明') : 'Description'}</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: textColor }}>{yinYangDesc}</p>
            </div>
          </div>
        );
      }
      case 'geju': {
        if (!geju) return renderEmpty();
        const levelColor = geju.level === '上格' ? '#C8A96E' : geju.level === '中上格' ? '#7BC67E' : geju.level === '中格' ? '#7A9ECF' : '#D14B45';
        return (
          <div className="space-y-3">
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-lg font-bold" style={{ color: textColor, fontFamily: "'Noto Serif TC', serif" }}>{geju.name}</span>
                <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: `${levelColor}20`, color: levelColor, border: `1px solid ${levelColor}40` }}>{geju.level}</span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${levelColor}15`, border: `2px solid ${levelColor}55` }}>
                  <span className="text-sm font-bold" style={{ color: levelColor }}>{geju.establishment != null ? `${geju.establishment}%` : '—'}</span>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-bold" style={{ color: textColor }}>{isZh ? trd('成立度') : 'Establishment'}</span>
                  <p className="text-[10px]" style={{ color: labelColor }}>{geju.tendencyText || geju.desc || '—'}</p>
                </div>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: textColor }}>{geju.desc}</p>
            </div>
            {geju.traits && geju.traits.length > 0 && (
              <div className="rounded-xl p-3" style={{ background: sectionBg }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <i className="ri-star-line text-xs" style={{ color: titleColor }} />
                  <span className="text-xs font-bold" style={{ color: titleColor }}>{isZh ? trd('格局特征') : 'Pattern Traits'}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {geju.traits.map((trait, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-md font-medium" style={{ background: `${titleColor}15`, color: titleColor, border: `1px solid ${titleColor}30` }}>{trait}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className="ri-briefcase-line text-xs" style={{ color: '#E8A020' }} />
                <span className="text-xs font-bold" style={{ color: '#E8A020' }}>{isZh ? trd('事业方向') : 'Career Direction'}</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: textColor }}>{geju.career}</p>
            </div>
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className="ri-heart-line text-xs" style={{ color: '#D14B45' }} />
                <span className="text-xs font-bold" style={{ color: '#D14B45' }}>{isZh ? trd('感情命理') : 'Love Destiny'}</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: textColor }}>{geju.love}</p>
            </div>
          </div>
        );
      }
      case 'xiyong': {
        if (!xiyong) return renderEmpty();
        return (
          <div className="space-y-3">
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-star-smile-line text-xs" style={{ color: '#4A90D9' }} />
                <span className="text-sm font-bold" style={{ color: '#4A90D9' }}>{isZh ? trd('喜用神') : 'Favorable Elements'}</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {xiyong.xi.map((item, i) => {
                  const color = item.color || ELEMENT_COLORS[item.element as Element] || '#888';
                  return (
                    <span key={i} className="text-sm px-2.5 py-1 rounded-full font-bold" style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>{item.element}</span>
                  );
                })}
              </div>
              <p className="text-sm leading-relaxed" style={{ color: textColor }}>{xiyong.advice}</p>
            </div>
            {xiyong.approaches && xiyong.approaches.length > 0 && (
              <div className="rounded-xl p-3" style={{ background: sectionBg }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <i className="ri-book-open-line text-xs" style={{ color: titleColor }} />
                  <span className="text-xs font-bold" style={{ color: titleColor }}>{isZh ? trd('取用依据') : 'Selection Basis'}</span>
                </div>
                <div className="space-y-1.5">
                  {xiyong.approaches.map((app, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs px-1.5 py-0.5 rounded font-bold" style={{ background: `${app.xi[0]?.color || titleColor}15`, color: app.xi[0]?.color || titleColor }}>{app.label}</span>
                      <span className="text-xs" style={{ color: labelColor }}>{app.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }
      case 'jishen': {
        if (!xiyong) return renderEmpty();
        return (
          <div className="space-y-3">
            <div className="rounded-xl p-3" style={{ background: sectionBg }}>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-error-warning-line text-xs" style={{ color: '#D14B45' }} />
                <span className="text-sm font-bold" style={{ color: '#D14B45' }}>{isZh ? trd('忌神') : 'Unfavorable Elements'}</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {xiyong.ji.map((item, i) => {
                  const color = item.color || ELEMENT_COLORS[item.element as Element] || '#888';
                  return (
                    <span key={i} className="text-sm px-2.5 py-1 rounded-full font-bold" style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>{item.element}</span>
                  );
                })}
              </div>
              <p className="text-sm leading-relaxed" style={{ color: textColor }}>
                {xiyong.ji.length > 0
                  ? trd('忌神代表命局中容易过量、失衡的五行能量。不是禁区，而是提醒：相关场景不宜长期作为主引擎。')
                  : trd('当前命局忌神不明显，各五行能量使用自由度较大。')}
              </p>
            </div>
            {xiyong.approaches?.map((app, i) => (
              app.ji.length > 0 && (
                <div key={i} className="rounded-xl p-3" style={{ background: sectionBg }}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-xs font-bold" style={{ color: '#D14B45' }}>{app.label}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {app.ji.map((item, j) => (
                      <span key={j} className="text-xs px-2 py-0.5 rounded-md font-medium" style={{ background: 'rgba(209,75,69,0.12)', color: '#D14B45', border: '1px solid rgba(209,75,69,0.3)' }}>{item.element}</span>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        );
      }
      default: return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4" style={{ background: overlayBg }} onClick={onClose}>
      <div className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[80dvh] sm:max-h-[85dvh]" style={{ background: cardBg, border: `1px solid ${cardBorder}` }} onClick={e => e.stopPropagation()}>
        {/* 拖拽把手（仅移动端） */}
        <div className="flex justify-center pt-2 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full" style={{ background: isDark ? 'rgba(183,154,197,0.3)' : 'rgba(232,160,32,0.25)' }} />
        </div>
        {/* 头部 */}
        <div className="px-4 pt-2 sm:pt-4 pb-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: sectionBg, color: titleColor, border: `1px solid ${cardBorder}` }}>
              <i className="ri-information-line" />
            </div>
            <h3 className="text-base font-bold" style={{ color: titleColor, fontFamily: "'Noto Serif TC', serif" }}>
              {titles[type]}
            </h3>
          </div>
          <button className="w-7 h-7 rounded-full flex items-center justify-center active:opacity-70 transition-opacity" style={{ background: sectionBg }} onClick={onClose}>
            <i className="ri-close-line text-sm" style={{ color: labelColor }} />
          </button>
        </div>
        {/* 内容区 */}
        <div className="px-4 pb-6 space-y-3 overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}